import React, { useRef } from "react";

import classNames from "classnames";
import { useDragLayer, useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";

import SortableItem from "./SortableItem";

import type { JSX } from "react";

import type { ItemDisposition, ZoneDisposition } from "./Disposition";
import type { ItemDragType } from "../../../../constants/dragTypes";

interface DroppableZoneProps {
  zone: ZoneDisposition;
  title: string;
  itemList: ItemDisposition[];
  onDrop: ({
    itemId,
    targetZone,
  }: {
    itemId: string;
    targetZone: ZoneDisposition;
  }) => void;
  acceptItemType: ItemDragType;
  onMove: ({
    itemId,
    newRank,
    targetZone,
  }: {
    itemId: string;
    newRank: number;
    targetZone: ZoneDisposition;
  }) => void;
}

function DroppableZone({
  zone,
  title,
  itemList,
  onDrop,
  acceptItemType,
  onMove,
}: DroppableZoneProps): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const dropRef = useRef<HTMLDivElement>(null);
  const [{ isOver }, dropConnector] = useDrop(
    () => ({
      accept: acceptItemType,
      hover: (item: { id: string }): void => {
        onDrop({ itemId: item.id, targetZone: zone });
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [zone, onMove, acceptItemType, onDrop],
  );

  dropConnector(dropRef);

  const { isDragging } = useDragLayer((monitor) => ({
    isDragging:
      monitor.isDragging() && monitor.getItemType() === acceptItemType,
  }));

  const dropzoneClassNames = classNames(
    "min-h-[105px] border-2 p-[0.3125rem] transition-all duration-200 flex gap-[0.46875rem] h-full overflow-x-auto rounded-[0.3125rem] border-dashed flex-col items-start",
    {
      "border-transparent": !isDragging,
      "border-primary": isDragging,
      "bg-primary/10": isOver,
      "bg-muted": !isOver,
    },
  );

  return (
    <div className="flex w-full flex-1 flex-col gap-[0.3125rem] overflow-hidden">
      <div ref={dropRef} className={dropzoneClassNames}>
        <div className="bg-surface-muted text-muted-foreground sticky left-0 rounded-[0.3125rem] p-[0.15625rem] px-[0.3125rem] text-[0.78125rem]">
          <div className="text-muted-foreground font-semibold">{title}</div>
        </div>
        <div className="relative flex flex-nowrap gap-[0.3125rem]">
          {itemList
            .sort((a, b) => a.rank - b.rank)
            .map((item) => (
              <SortableItem
                key={item.id}
                acceptItemType={acceptItemType}
                item={item}
                onMove={onMove}
                targetZone={zone}
              />
            ))}
        </div>
        {itemList.length === 0 && (
          <div className="text-muted-foreground flex size-full items-center justify-center italic">
            {t("dropItems")}
          </div>
        )}
      </div>
    </div>
  );
}

export default DroppableZone;
